<template>
  <div class="d-flex flex-column">
    <span class="mb-2">{{ props.text }}</span>
    <div class="image-input__container">
      <input
        ref="fileInput"
        class="file-upload"
        type="file"
        accept="image/png"
        @change="onFileChanged"
      />
      <div
        class="image-input__wrapper"
        :style="{
          'background-image': `url(${fileData})`,
        }"
      />
      <div class="image-input__overlay" :class="{ 'd-flex': loading }">
        <CButton v-if="!loading" color="primary" @click="fileInput.click()">
          <strong>{{ props.uploadText }}</strong>
        </CButton>
        <CSpinner v-else />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { watchEffect, ref, type Ref } from "vue-demi"
import axios from "axios"
import { useI18n } from "vue-i18n"
import { fileStore, toastStore } from "@/store"
import { errorHandler } from "@/libraries/helpers"

const i18n = useI18n()

const props = withDefaults(defineProps<{
  text?: string
  uploadText?: string
  defaultImage: any
  imageId?: number
}>(), {
  text: "Image",
  uploadText: "Upload Image",
})

const emit = defineEmits(["uploaded"])

const fileInput = ref<Ref>(null)

const fileData = ref(props.defaultImage)
const loading = ref(false)

watchEffect(() => {
  if (props.imageId) {
    loading.value = true
    axios
      .get(`/v1/files/${props.imageId}/data`, {
        responseType: "blob",
        headers: {
          "x-no-sw-cache": true,
        },
      })
      .then(response => {
        fileData.value = window.URL.createObjectURL(response.data)
      })
      .catch()
      .finally(() => (loading.value = false))
  }
})

const onFileChanged = async (event: any) => {
  const file = event.target.files[0]
  if (!file) return
  try {
    loading.value = true
    const data = await fileStore.add(file)
    emit("uploaded", data)
  } catch (error: any) {
    const { response } = error
    const message = errorHandler.generateErrorMessage(response.status) || response?.data?.status

    toastStore.toasts.push({ color: "danger", message: i18n.t("failed_to_upload", { message }) })
    loading.value = false
  }
}
</script>

<style scoped lang="scss">
.file-upload {
  display: none;
}
.image-input {
  &__container {
    position: relative;
    display: flex;
    &:hover {
      .image-input__overlay {
        display: flex;
      }
    }
  }
  &__wrapper {
    height: 150px;
    border: 1px dashed;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 100%;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    img {
      height: 150px;
    }
  }
  &__text {
    font-size: 1.25rem;
    font-weight: 500;
  }
  &__overlay {
    position: absolute;
    background: rgba(0, 0, 0, 0.2);
    width: 100%;
    height: 100%;
    display: none;
    justify-content: center;
    align-items: center;
  }
}
</style>
